// Minimal vanilla FE: app auth, save/verify provider creds, MFA dialog, status.
const app = document.getElementById("app");

async function api(path, opts = {}) {
  const res = await fetch(path, {
    headers: { "content-type": "application/json" },
    ...opts,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `HTTP ${res.status}`);
    err.status = res.status; // callers can branch (e.g. 503 session_expired)
    err.detail = data.detail;
    throw err;
  }
  return data;
}

const el = (html) => { const t = document.createElement("template"); t.innerHTML = html.trim(); return t.content.firstElementChild; };
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

// ---- screens ----------------------------------------------------------
async function init() {
  try {
    const me = await api("/api/me");
    renderDashboard(me);
  } catch {
    renderLogin();
  }
}

function renderLogin() {
  app.innerHTML = "";
  const card = el(`
    <div class="card" style="max-width:360px;margin:60px auto">
      <h1>NSD Insurance Exchange</h1>
      <p class="sub">Sign in to continue</p>
      <label>Username</label><input id="u" autocomplete="username" />
      <label>Password</label><input id="p" type="password" autocomplete="current-password" />
      <button id="go">Sign in</button>
      <div class="err" id="err"></div>
    </div>`);
  app.appendChild(card);
  const go = async () => {
    try {
      await api("/api/auth/login", { method: "POST", body: { username: card.querySelector("#u").value, password: card.querySelector("#p").value } });
      init();
    } catch (e) { card.querySelector("#err").textContent = e.message; }
  };
  card.querySelector("#go").onclick = go;
  card.querySelector("#p").addEventListener("keydown", (e) => e.key === "Enter" && go());
}

// provider catalog — loaded from the registry via /api/providers (single source of
// truth; adding a provider on the backend shows up here with no FE edit).
let PROVIDERS = []; // [{ id, label, eligibility, benefits }]
const providerLabel = (v) => PROVIDERS.find((p) => p.id === v)?.label || v;

async function renderDashboard(me) {
  app.innerHTML = "";
  PROVIDERS = await api("/api/providers"); // registry-driven catalog
  app.appendChild(el(`
    <div class="topbar">
      <div><h1>Insurance accounts</h1><p class="sub" style="margin:0">Signed in as ${esc(me.username)}</p></div>
      <div class="flex">
        <button id="add" style="margin:0">+ Add account</button>
        <button class="ghost" id="logout" style="margin:0">Log out</button>
      </div>
    </div>`));
  app.querySelector("#logout").onclick = async () => { await api("/api/auth/logout", { method: "POST" }); init(); };

  // accounts list
  const listCard = el(`<div class="card"><h2>Accounts</h2><div id="list"></div></div>`);
  app.appendChild(listCard);
  const refresh = () => refreshList(listCard.querySelector("#list"));
  await refresh();
  app.querySelector("#add").onclick = () => openAddDialog(refresh);

  // recent lookups (service audit log)
  const logCard = el(`
    <div class="card">
      <div class="topbar" style="margin-bottom:12px">
        <h2 style="margin:0">Recent lookups</h2>
        <button class="ghost" id="reflog" style="margin:0">Refresh</button>
      </div>
      <div id="log"></div>
    </div>`);
  app.appendChild(logCard);
  const refreshLog = () => refreshLookups(logCard.querySelector("#log"));
  await refreshLog();
  logCard.querySelector("#reflog").onclick = refreshLog;
}

const fmtTime = (ms) => new Date(ms).toLocaleString();

// recent eligibility/benefits calls from /api/requests (the request_log audit table)
async function refreshLookups(container) {
  const rows = await api("/api/requests?limit=50");
  container.innerHTML = "";
  if (!rows.length) { container.appendChild(el(`<p class="muted">No lookups yet. The backend writes a row here on every eligibility/benefits call.</p>`)); return; }
  const table = el(`
    <table class="log">
      <thead><tr><th>Time</th><th>Provider</th><th>Member</th><th>Type</th><th>Result</th><th>Status</th><th>ms</th></tr></thead>
      <tbody></tbody>
    </table>`);
  const tb = table.querySelector("tbody");
  for (const r of rows) {
    const cls = r.status === 503 ? "warn" : r.status >= 400 ? "bad" : "ok";
    // 503 = session_expired contract → nudge re-login; else verdict, else the error
    const result = r.status === 503 ? "session expired — re-login" : (r.verdict || r.error || "—");
    tb.appendChild(el(`
      <tr>
        <td class="muted">${esc(fmtTime(r.ts))}</td>
        <td>${esc(providerLabel(r.provider))}</td>
        <td>${esc(r.memberId || "—")}</td>
        <td>${esc(r.kind)}</td>
        <td>${esc(result)}</td>
        <td><span class="stat ${cls}">${esc(String(r.status))}</span></td>
        <td class="muted">${esc(String(r.latencyMs))}</td>
      </tr>`));
  }
  container.appendChild(table);
}

// Add-account dialog: pick provider, enter creds, save → kick off login (+MFA).
function openAddDialog(onDone) {
  const opts = PROVIDERS.map((p) => `<option value="${p.id}">${esc(p.label)}</option>`).join("");
  const modal = showModal(`
    <h2>Add insurance account</h2>
    <label>Provider</label>
    <select id="prov">${opts}</select>
    <label>Username</label><input id="u" autocomplete="off" />
    <label>Password</label><input id="p" type="password" autocomplete="off" />
    <div class="flex">
      <button id="save">Save &amp; log in</button>
      <button class="ghost" id="cancel">Cancel</button>
    </div>
    <div class="err" id="err"></div>`);
  modal.querySelector("#cancel").onclick = () => closeModal(modal);
  modal.querySelector("#save").onclick = async () => {
    const provider = modal.querySelector("#prov").value;
    const username = modal.querySelector("#u").value.trim();
    const password = modal.querySelector("#p").value;
    if (!username || !password) { modal.querySelector("#err").textContent = "Username and password required"; return; }
    try {
      const { id } = await api("/api/accounts", { method: "POST", body: { provider, username, password } });
      closeModal(modal);
      await doLogin(id, onDone); // opens its own modal; prompts for MFA if the provider asks
    } catch (e) { modal.querySelector("#err").textContent = e.message; }
  };
}

function statusBadge(a) {
  if (!a.hasSession) return `<span class="badge none">never logged in</span>`;
  if (a.status === "active") return `<span class="badge active">active</span>`;
  if (a.status === "needs_mfa") return `<span class="badge expired">re-login (MFA)</span>`;
  return `<span class="badge expired">session expired</span>`;
}

async function refreshList(container) {
  const accounts = await api("/api/accounts");
  container.innerHTML = "";
  if (!accounts.length) { container.appendChild(el(`<p class="muted">No accounts yet. Add one below.</p>`)); return; }
  for (const a of accounts) {
    const needsLogin = !a.hasSession || a.status === "expired" || a.status === "needs_mfa";
    const row = el(`
      <div class="row">
        <div>
          <div>${esc(a.username)} <span class="muted">· ${esc(providerLabel(a.provider))}</span></div>
          <div style="margin-top:4px">${statusBadge(a)}</div>
        </div>
        <div class="flex">
          <button class="${needsLogin ? "" : "secondary"}" data-act="login">${needsLogin ? "Log in" : "Re-login"}</button>
          <button class="ghost danger" data-act="delete" title="Delete account">Delete</button>
        </div>
      </div>`);
    row.querySelector('[data-act="login"]').onclick = () => doLogin(a.id, () => refreshList(container));
    row.querySelector('[data-act="delete"]').onclick = async () => {
      if (!confirm(`Delete the ${providerLabel(a.provider)} account "${a.username}"? This removes its saved credentials and session.`)) return;
      await api(`/api/accounts/${a.id}`, { method: "DELETE" });
      await refreshList(container);
    };
    container.appendChild(row);
  }
}

// ---- login flow with MFA dialog --------------------------------------
async function doLogin(accountId, onDone) {
  let modal = showModal(`<h2>Logging in…</h2><p class="muted" id="msg">Starting browser session…</p>`);
  let mfaSubmitted = false;
  let jobId = null;
  // If the operator refreshes / closes the tab mid-login, abort the job so the
  // worker stops and the login lock frees. sendBeacon fires during unload and
  // carries the session cookie; the cancel endpoint is a no-op on finished jobs.
  const onUnload = () => { if (jobId) navigator.sendBeacon(`/api/login/${jobId}/cancel`); };
  window.addEventListener("pagehide", onUnload);
  try {
    ({ jobId } = await api(`/api/accounts/${accountId}/login`, { method: "POST" }));
    for (;;) {
      await new Promise((r) => setTimeout(r, 2000));
      const job = await api(`/api/login/${jobId}`);
      const msg = modal.querySelector("#msg");
      if (job.state === "running" || job.state === "pending") { if (msg) msg.textContent = "Entering credentials…"; }
      else if (job.state === "awaiting_mfa" && !mfaSubmitted) {
        mfaSubmitted = true;
        const code = await promptMfa(modal, job.maskedEmail);
        if (code == null) { // operator cancelled → abort the job so the worker stops + frees the login lock
          await api(`/api/login/${jobId}/cancel`, { method: "POST" }).catch(() => {});
          closeModal(modal); return;
        }
        await api(`/api/login/${jobId}/mfa`, { method: "POST", body: { code } });
        modal.innerHTML = `<h2>Verifying…</h2><p class="muted" id="msg">Submitting code…</p>`;
      } else if (job.state === "done") { closeModal(modal); await onDone(); return; }
      else if (job.state === "failed") { modal.innerHTML = `<h2>Login failed</h2><p class="err">${esc(job.error || "unknown error")}</p><button id="ok">Close</button>`; modal.querySelector("#ok").onclick = () => { closeModal(modal); onDone(); }; return; }
    }
  } catch (e) {
    modal.innerHTML = `<h2>Login failed</h2><p class="err">${esc(e.message)}</p><button id="ok">Close</button>`;
    modal.querySelector("#ok").onclick = () => { closeModal(modal); onDone(); };
  } finally {
    window.removeEventListener("pagehide", onUnload);
  }
}

function promptMfa(modal, maskedEmail) {
  return new Promise((resolve) => {
    modal.innerHTML = `
      <h2>Enter MFA code</h2>
      <p class="muted">A verification code was sent to ${esc(maskedEmail || "your email")}.</p>
      <label>Code</label><input id="code" inputmode="numeric" autocomplete="one-time-code" />
      <div class="flex">
        <button id="submit">Verify</button>
        <button class="ghost" id="cancel">Cancel</button>
      </div>`;
    const input = modal.querySelector("#code");
    input.focus();
    const submit = () => { const v = input.value.trim(); if (v) resolve(v); };
    modal.querySelector("#submit").onclick = submit;
    input.addEventListener("keydown", (e) => e.key === "Enter" && submit());
    modal.querySelector("#cancel").onclick = () => resolve(null);
  });
}

function showModal(html) {
  const bg = el(`<div class="modal-bg"><div class="modal">${html}</div></div>`);
  document.body.appendChild(bg);
  return bg.querySelector(".modal");
}
function closeModal(modal) { modal.closest(".modal-bg").remove(); }

init();
